/**
 * Report an incident or near miss, from a shift's job pack (crew) or the office. After sending, the
 * report shows the prompts from the client layer (urgent ones first), each ticked with the time, and
 * photos can be added. Near-miss categories and every prompt's wording come from the client layer.
 */
import { useState, type FormEvent } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { Chip } from "../../components/States";
import { ApiError, isQueued, QUEUED_MESSAGE } from "../../lib/api";
import { queueAfter } from "../crew-api";
import { BRAND } from "../../lib/config";
import { fmtDate, localInstant } from "../../lib/format";
import { telHref } from "../../lib/route";
import type { Incident, IncidentKind, IncidentPrompt, Injury } from "../../lib/supabase";
import { addIncidentPhoto, reportFromOffice, reportFromShift, tickPrompt, type IncidentInput } from "./field-data";

export const MAX_PHOTOS = 5;
export const INJURY_LABEL: Record<Injury, string> = { none: "None", first_aid: "First aid", medical_treatment: "Medical treatment", hospital: "Hospital" };
export const KIND_LABEL: Record<IncidentKind, string> = { incident: "Incident", near_miss: "Near miss" };

const nowLocal = () => {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: BRAND.timezone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
};

/** Where the report is made from: a shift's job pack (crew), or the office (optionally about a shift or job). */
export type ReportFrom = { crewShift: string } | { office: { bookingId?: string; jobId?: string } };

export function IncidentReport({ from, onClose, onSent }: { from: ReportFrom; onClose: () => void; onSent?: (i: Incident) => void }) {
  const cfg = BRAND.field?.incidents;
  const [f, setF] = useState({
    kind: "near_miss" as IncidentKind, when: nowLocal(), location: "", description: "", peopleInvolved: "", injury: "none" as Injury,
    immediateAction: "", category: "", categoryOther: "", notifiable: null as boolean | null,
  });
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ incident: Incident; officeAlerted: boolean; photoProblems: number } | null>(null);
  const [saved, setSaved] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const as = "crewShift" in from ? "crew" : "office";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (f.notifiable === null) return setNotice({ tone: "error", text: `Answer: ${cfg?.notifiableQuestion ?? "is this a notifiable incident?"}` });
    if (f.kind === "near_miss" && !f.category) return setNotice({ tone: "error", text: "Choose what kind of near miss it was." });
    const [ymd, hm] = f.when.split("T") as [string, string];
    const input: IncidentInput = {
      kind: f.kind, occurredAt: localInstant(ymd, hm).toISOString(), description: f.description.trim(), injury: f.injury, notifiable: f.notifiable,
      ...(f.location.trim() ? { location: f.location.trim() } : {}),
      ...(f.peopleInvolved.trim() ? { peopleInvolved: f.peopleInvolved.trim() } : {}),
      ...(f.immediateAction.trim() ? { immediateAction: f.immediateAction.trim() } : {}),
      ...(f.kind === "near_miss" ? { category: f.category, ...(f.category === "other" ? { categoryOther: f.categoryOther.trim() } : {}) } : {}),
    };
    setBusy(true);
    setNotice(null);
    try {
      const res = "crewShift" in from ? await reportFromShift(from.crewShift, input) : await reportFromOffice({ ...input, ...from.office });
      // The report is safe; photos follow one at a time so a weak signal only loses a photo, never the report.
      let photoProblems = 0;
      for (const p of photos) {
        try {
          await addIncidentPhoto(as, res.incident.id, p);
        } catch {
          photoProblems++;
        }
      }
      setSent({ ...res, photoProblems });
      onSent?.(res.incident);
    } catch (err) {
      // No signal: the report is saved on the phone, and its photos follow it once it has been sent.
      const actionId = isQueued(err) && err instanceof ApiError ? (err.body?.actionId as string | undefined) : undefined;
      if (actionId && "crewShift" in from) {
        for (const p of photos) {
          const f = new FormData();
          f.set("file", p);
          await queueAfter(`/incidents/{${actionId}.incident.id}/photos`, { method: "POST", body: f }, "Incident photo", from.crewShift).catch(() => undefined);
        }
        setSaved(true);
      } else fail(err);
    } finally {
      setBusy(false);
    }
  }

  if (saved) {
    return (
      <div className="stack">
        <p className="notice notice--ok" role="status"><Icon name="check" size={20} />{QUEUED_MESSAGE}</p>
        <p className="strong">If anyone is hurt or in danger, call 000 now.</p>
        <button type="button" className="btn btn--outline btn--block" onClick={onClose}>Close</button>
      </div>
    );
  }
  if (sent) return <AfterReport as={as} incident={sent.incident} officeAlerted={sent.officeAlerted} photoProblems={sent.photoProblems} onClose={onClose} />;

  const categories = cfg?.nearMissCategories ?? [];
  return (
    <form className="form stack" onSubmit={submit}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="yesno" role="radiogroup" aria-label="What are you reporting?">
        {(["near_miss", "incident"] as const).map((k) => (
          <button key={k} type="button" role="radio" aria-checked={f.kind === k} className={`btn btn--sm ${f.kind === k ? "btn--ink" : "btn--outline"}`} onClick={() => setF((x) => ({ ...x, kind: k }))}>{KIND_LABEL[k]}</button>
        ))}
      </div>
      {f.kind === "near_miss" && (
        <label className="field">
          <span className="field__label">What kind of near miss</span>
          <select className="input input--lg" value={f.category} onChange={set("category")} required>
            <option value="">Choose one</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            <option value="other">Other</option>
          </select>
        </label>
      )}
      {f.kind === "near_miss" && f.category === "other" && (
        <label className="field"><span className="field__label">Say what kind</span><input className="input input--lg" required value={f.categoryOther} onChange={set("categoryOther")} /></label>
      )}
      <label className="field"><span className="field__label">What happened</span><textarea className="input" rows={4} required minLength={3} value={f.description} onChange={set("description")} /></label>
      <label className="field"><span className="field__label">When</span><input className="input input--lg" type="datetime-local" required value={f.when} max={nowLocal()} onChange={set("when")} /></label>
      <label className="field"><span className="field__label">Where on site</span><input className="input input--lg" value={f.location} onChange={set("location")} placeholder="e.g. North end, near the crane gate" /></label>
      <label className="field"><span className="field__label">People involved</span><input className="input input--lg" value={f.peopleInvolved} onChange={set("peopleInvolved")} /></label>
      <label className="field">
        <span className="field__label">Injuries</span>
        <select className="input input--lg" value={f.injury} onChange={set("injury")}>{(Object.keys(INJURY_LABEL) as Injury[]).map((k) => <option key={k} value={k}>{INJURY_LABEL[k]}</option>)}</select>
      </label>
      <label className="field"><span className="field__label">Action taken straight away</span><textarea className="input" rows={2} value={f.immediateAction} onChange={set("immediateAction")} /></label>
      <fieldset className="checkitem">
        <legend className="checkitem__label">{cfg?.notifiableQuestion ?? "Is this a notifiable incident?"}</legend>
        {cfg?.notifiableHelp && <span className="small muted">{cfg.notifiableHelp}</span>}
        <div className="yesno" role="radiogroup" aria-label={cfg?.notifiableQuestion ?? "Notifiable"}>
          {([true, false] as const).map((v) => (
            <button key={String(v)} type="button" role="radio" aria-checked={f.notifiable === v} className={`btn btn--sm ${f.notifiable === v ? (v ? "btn--primary" : "btn--ink") : "btn--outline"}`} onClick={() => setF((x) => ({ ...x, notifiable: v }))}>{v ? "Yes" : "No"}</button>
          ))}
        </div>
      </fieldset>
      <label className="field">
        <span className="field__label">Photos (up to {MAX_PHOTOS})</span>
        <input className="input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(e) => setPhotos([...(e.target.files ?? [])].slice(0, MAX_PHOTOS))} />
        {photos.length > 0 && <span className="field__hint">{photos.length === 1 ? "1 photo" : `${photos.length} photos`} will be added.</span>}
      </label>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary btn--block" disabled={busy} aria-busy={busy}>{busy ? "Sending" : "Send the report"}</button>
        <button type="button" className="btn btn--outline btn--block" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

function AfterReport({ as, incident, officeAlerted, photoProblems, onClose }: { as: "crew" | "office"; incident: Incident; officeAlerted: boolean; photoProblems: number; onClose: () => void }) {
  return (
    <div className="stack">
      <p className="notice notice--ok" role="status"><Icon name="check" size={18} />Report {incident.reference} sent.{officeAlerted ? " The office has been emailed." : ""}</p>
      {photoProblems > 0 && <p className="notice notice--error" role="alert"><Icon name="warning" size={18} />{photoProblems === 1 ? "1 photo" : `${photoProblems} photos`} did not upload. Send them to the office.</p>}
      <Prompts as={as} incident={incident} />
      <button type="button" className="btn btn--outline btn--block" onClick={onClose}>Done</button>
    </div>
  );
}

/** The prompts on a report, urgent ones first, each ticked with the time. */
export function Prompts({ as, incident, onChange }: { as: "crew" | "office"; incident: Incident; onChange?: (i: Incident) => void }) {
  const [prompts, setPrompts] = useState<IncidentPrompt[]>(incident.prompts);
  const [busy, setBusy] = useState<string | null>(null);
  const { notice, setNotice, fail } = useNotice();
  if (!prompts.length) return null;
  const ordered = [...prompts.filter((p) => p.when === "notifiable"), ...prompts.filter((p) => p.when !== "notifiable")];

  async function toggle(p: IncidentPrompt) {
    setBusy(p.id);
    setNotice(null);
    try {
      const saved = await tickPrompt(as, incident.id, p.id, !p.doneAt);
      setPrompts(saved.prompts);
      onChange?.(saved);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="stack">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <h3 className="card__title">Now do these</h3>
      <ul className="prompts">
        {ordered.map((p) => (
          <li key={p.id} className={p.when === "notifiable" ? "prompts__urgent" : undefined}>
            <span className="strong">{p.label}</span>
            <span className="prompts__actions">
              {p.phone && <a className="btn btn--primary btn--sm" href={telHref(p.phone)}>Call {p.phone}</a>}
              <button type="button" className={`btn btn--sm ${p.doneAt ? "btn--ink" : "btn--outline"}`} aria-pressed={Boolean(p.doneAt)} disabled={busy === p.id} onClick={() => void toggle(p)}>
                {p.doneAt ? <><Icon name="check" size={16} /> Done {fmtDate(p.doneAt)}</> : "Mark done"}
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const incidentChip = (kind: IncidentKind, notifiable: boolean) => (
  <span className="nowrap"><Chip tone={kind === "incident" ? "primary" : "accent"}>{KIND_LABEL[kind]}</Chip>{notifiable && <> <Chip tone="primary">Notifiable</Chip></>}</span>
);
